import { useCallback } from 'react';
import {
  useKeyValueSettingsMutation,
  updateCache
} from '~/modules/common/hooks';
import { mapToTimesheetAccessInput } from '~/modules/tasks/components/TaskDrawer/EditTask/useFormState';
import { objectToUTCString } from '~/modules/common/dates/convert';
import { ADD_TASK_DIALOG_SETTINGS } from '../constants';

const getTimeAndExpenseEntryTypeForTask = ({ values, project }) => {
  if (project?.billingType?.id === 'urn:replicon:billing-type:non-billable')
    return undefined;

  return values.timeAndExpenseEntryType;
};

const handleAllocationSettingUpdate = ({
  isPsaRmpTaskAllocation1Enabled,
  initialAllocateResourceTimeValue,
  currentAllocateResourceTimeValue,
  putKeyValueSettings
}) => {
  if (
    isPsaRmpTaskAllocation1Enabled &&
    initialAllocateResourceTimeValue !== currentAllocateResourceTimeValue
  ) {
    putKeyValueSettings({
      variables: {
        input: {
          key: ADD_TASK_DIALOG_SETTINGS,
          settings: {
            isCreateAllocationEnabled: currentAllocateResourceTimeValue
          }
        }
      }
    });
  }
};

export const useOnSubmit = ({
  validateTaskNameMutation,
  createTask,
  onClose,
  setMessage,
  parentUri,
  customFieldDefinitions,
  project,
  isPsaRmpTaskAllocation1Enabled,
  shouldRollupTaskDates
}) => {
  const [putKeyValueSettings] = useKeyValueSettingsMutation(
    updateCache(ADD_TASK_DIALOG_SETTINGS)
  );

  return useCallback(
    async (values, { setSubmitting, handleReset, setFieldError }) => {
      setSubmitting(true);
      const {
        name,
        code,
        startDate,
        endDate,
        assignedUser,
        assignedRole,
        description,
        estimatedHours,
        estimatedCost,
        isMilestone,
        isTimeEntryAllowed,
        assignedUserRoleId,
        resourceAllocations = [],
        copyParentTaskResourceAllocations,
        timesheetAccess,
        resourceEstimates,
        initialAllocateResourceTimeValue,
        currentAllocateResourceTimeValue,
        ...customFieldValues
      } = values;

      const {
        data: {
          validateTaskName: { isValid }
        }
      } = await validateTaskNameMutation({
        variables: {
          input: {
            parentId: parentUri,
            taskName: name
          }
        }
      });

      if (!isValid) {
        setFieldError('nameExists', true);
        setSubmitting(false);
      } else {
        const hasEstimatedCostAmount = estimatedCost && estimatedCost.amount;

        const error = await createTask({
          parentUri,
          name,
          code,
          startDate: startDate ? objectToUTCString(startDate) : null,
          endDate: endDate ? objectToUTCString(endDate) : null,
          estimatedHours: estimatedHours ? Number(estimatedHours) : null,
          estimatedCost: hasEstimatedCostAmount ? estimatedCost : null,
          assignedUser,
          assignedRole,
          description,
          isMilestone,
          isTimeEntryAllowed,
          timeAndExpenseEntryType: getTimeAndExpenseEntryTypeForTask({
            values,
            project
          }),
          timesheetAccessUris: mapToTimesheetAccessInput(timesheetAccess),
          customFields: constructCustomFields(
            customFieldDefinitions,
            customFieldValues
          ),
          assignedUserRoleId,
          resourceAllocations: resourceAllocations.map(a => ({
            resourceId: a.resource.id,
            allocatedHours: a.allocatedHours
          })),
          doNotCopyParentTaskResourceAllocations: !copyParentTaskResourceAllocations,
          shouldRollupTaskDates,
          resourceEstimates: (resourceEstimates || []).map(a => ({
            resourceUserId: a.resource?.id,
            projectRoleId: a.role?.id,
            initialEstimatedHours: a.estimatedHours,
            addTaskAllocation: a.isCreateAllocationEnabled
          }))
        });

        setSubmitting(false);

        if (!error) {
          handleAllocationSettingUpdate({
            isPsaRmpTaskAllocation1Enabled,
            initialAllocateResourceTimeValue,
            currentAllocateResourceTimeValue,
            putKeyValueSettings
          });
          onClose();
          handleReset();
          setMessage(name);
        }

        setFieldError('submitError', error);
      }
    },
    [
      validateTaskNameMutation,
      parentUri,
      createTask,
      project,
      customFieldDefinitions,
      shouldRollupTaskDates,
      isPsaRmpTaskAllocation1Enabled,
      putKeyValueSettings,
      onClose,
      setMessage
    ]
  );
};

const constructCustomFields = (customFieldDefinitions, customFieldValues) =>
  customFieldDefinitions.map(definition => {
    const customField = { customField: { id: definition.uri } };

    switch (definition.type.uri) {
      case 'urn:replicon:custom-field-type:text': {
        customField.text = customFieldValues[definition.uri];
        break;
      }
      case 'urn:replicon:custom-field-type:numeric': {
        customField.number = customFieldValues[definition.uri];
        break;
      }
      case 'urn:replicon:custom-field-type:date': {
        customField.date = customFieldValues[definition.uri];
        break;
      }
      case 'urn:replicon:custom-field-type:drop-down': {
        customField.dropDownOptionUri = customFieldValues[definition.uri];
        break;
      }
      default:
        customField.text = customFieldValues[definition.uri];
        break;
    }

    customField.definition = definition;

    return customField;
  });

export default useOnSubmit;
